import Phaser from "phaser";
import { GAME, HERO, LAYOUT } from "@/config";
import { TIERS, type CareTierId } from "@/data/tunables";
import { PALETTE, PALETTE_HEX } from "@/palette";
import { buildHero, fitFacilityName, HERO_THEME } from "./hero";
import { wirePan } from "./pan";
import { artTextureKey, isArtLoaded, resolvePatientTexture } from "@/sprites/artManifest";
import {
  DOLLHOUSE,
  type TierDollhouse,
  PHP_TOP,
  PHP_CASE_H,
  PHP_CLASS_TOP,
  PHP_CLASS_H,
  PHP_BUGGY_TOP,
  PHP_BUGGY_H,
  IOP_TOP,
  IOP_CHECK_H,
  SOBER_TOP,
  SOBER_BED_H,
  SOBER_KITCH_TOP,
  SOBER_KITCH_H,
  SOBER_BATH_TOP,
  SOBER_BATH_H,
  SOBER_TABLE,
  IOP_GROUP_TOP,
  IOP_GROUP_H,
  IOP_LOT_TOP,
  IOP_LOT_H,
  RESI_GROUP_TOP,
  RESI_GROUP_H,
  RESI_TOP,
  RESI_BREAK_H,
  RESI_CAFE_TOP,
  RESI_CAFE_H,
  DETOX_PANEL_MED_H,
  DETOX_PANEL_SUB_H,
  DETOX_PANEL_WARD_H,
  DETOX_SUB_TOP,
  DETOX_TOP,
  DETOX_WARD_TOP,
} from "@/data/visuals";

/** Which hero art slug + gag motto each care tier uses. */
const HERO_SLUG: Record<CareTierId, string> = {
  detox: "detox",
  residential: "residential",
  php: "php",
  iop: "iop",
  soberLiving: "sober",
};
const HERO_MOTTO: Record<CareTierId, string> = {
  detox: "DRY OUT, CASH IN",
  residential: "OUR HOUSE, YOUR BILL",
  php: "PROGRESS, BILLED HOURLY",
  iop: "RECOVERY, PART-TIME",
  soberLiving: "RENT IS RECOVERY",
};
import { collectLoot,
  patientInSlot,
  tendSlot,
  treatSlot,
} from "@/systems/actions";
import { effectiveCapacity } from "@/state/GameState";
import { relapseBreakdown, type TickEvent } from "@/systems/engine";
import { runtime } from "@/systems/runtime";
import { bakeAllCharacterTextures, Character } from "@/sprites/Character";
import { bakeJuiceTextures, cashRain, debris, flash, flowerBurst, shake } from "@/sprites/juice";
import { installTierActions, showGagModal, type OverlayHandles } from "@/ui/dom";
import { BREAK_VIGNETTES, BUGGY_BICKERS, BUGGY_RIDERS, CASE_ACTORS, CASE_RED_TAPE, CHORE_GRUMBLES, HOUSE_MEETING_LINES, IOP_CHECKIN_LINES, IOP_FACILITATOR_LINES, IOP_GROUP_SIGHS, LOT_EXCUSES, ROOMMATE_SCRIPT, STUDENT_QUESTIONS, THERAPIST_LESSONS, FACILITATOR_PLATITUDES, MED_NURSE_REPLIES, MED_WINDOW_SYMPTOMS, SERVER_SPECIALS, TAP_GAGS, WARD_NURSE_MUTTERS } from "@/data/tapGags";
import type { Patient } from "@/state/GameState";

/**
 * ONE parameterized floor scene serves all five care tiers (v0.2 RFC §8):
 * per-tier title/gag/verb from the tier table; slot grid sized by capacity;
 * tap slot = tend (per-tier verb), TREAT chip on ISSUE; relapse-% panel is
 * the M8 transparency window. Registered once per tier as `tier-<id>`.
 */

const SLOT_W = 300;
const SLOT_H = 150;
const COLS = 3;
const COL_XS = [280, 660, 1040];
const ROW_DY = 420;
const EXIT_RIGHT = GAME.width + 140;

interface SlotView {
  x: number;
  y: number;
  /** Dollhouse cot image (detox): texture flips empty <-> occupied. */
  cot: Phaser.GameObjects.Image | undefined;
  scale: number;
  /** Per-slot texture pair, resolved once at build (never re-derived by
   * float-coordinate equality — leg-1 review finding, v0.9). */
  emptyTex: string;
  occupiedTex: string;
  sprite: Character | undefined;
  patientId: number | undefined;
  lastState: string | undefined;
  barFrame: Phaser.GameObjects.Rectangle;
  barFill: Phaser.GameObjects.Rectangle;
  treatChip: Phaser.GameObjects.Container;
}

export class TierScene extends Phaser.Scene {
  readonly tierId: CareTierId;
  private overlay!: OverlayHandles;
  private slots: SlotView[] = [];
  private staffSprite: Character | null = null;
  private staffBadge: Phaser.GameObjects.Text | null = null;
  private riskPanel: Phaser.GameObjects.Text | null = null;
  /** v0.9 dollhouse staging: per-tier config, engaged when its art is loaded. */
  private dollhouse = false;
  private dh: TierDollhouse | null = null;
  private subShade: Phaser.GameObjects.Rectangle | null = null;
  private nurse: Character | null = null;
  private lobbyist: Character | null = null;
  private facilityBar: Phaser.GameObjects.Text | null = null;
  private phpBoard: Phaser.GameObjects.Image | null = null;
  private phpStool: Phaser.GameObjects.Image | null = null;
  private unsubscribe: (() => void) | undefined;
  /** v0.13: the tier's single active loot sprite (id → container). */
  private lootSprite: { id: number; obj: Phaser.GameObjects.Container } | undefined;

  constructor(tierId: CareTierId) {
    super(`tier-${tierId}`);
    this.tierId = tierId;
  }

  init(data: { overlay: OverlayHandles }): void {
    this.overlay = data.overlay;
  }

  create(): void {
    const cfg = DOLLHOUSE[this.tierId];
    this.dh = cfg && cfg.requiredArt.every((k) => isArtLoaded(k)) ? cfg : null;
    this.dollhouse = this.dh !== null;
    // create() re-runs on scene restart (New Game / prestige) — every
    // one-shot guard and ambient-actor ref must reset or the whole speech
    // layer dies on the second life (leg-1 review finding, v0.9).
    this.platitudesRunning = false;
    this.lessonsRunning = false;
    this.theaterRunning = false;
    this.facilitatingRunning = false;
    this.meetingRunning = false;
    this.nurse = null;
    this.lobbyist = null;
    this.iopFacilitator = null;
    this.subShade = null;
    this.phpBoard = null;
    this.phpStool = null;
    this.cameras.main.setBackgroundColor(PALETTE_HEX.cream);
    bakeAllCharacterTextures(this);
    bakeJuiceTextures(this);
    this.slots = [];
    this.drawStage();
    this.syncSlotCount();
    this.reconcile();

    // v0.13: a loot drop may already be active for this tier (reload,
    // or spawned while another scene was up) — restore its sprite. A
    // SLEPT scene drops event batches, so the same sync runs on WAKE
    // (bot leg: loot spawned while slept had no sprite on return).
    this.syncLootFromState();
    this.events.on(Phaser.Scenes.Events.WAKE, this.syncLootFromState, this);
    this.unsubscribe = runtime.subscribe((events) => {
      if (!this.scene.isActive()) return;
      this.onEvents(events);
      this.reconcile();
    });
    const onWake = () => {
      this.syncSlotCount(); // capacity letters may have raised beds while asleep
      this.reconcile();
      this.installActions();
    };
    this.events.on(Phaser.Scenes.Events.WAKE, onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.events.off(Phaser.Scenes.Events.WAKE, onWake);
    });
    this.installActions();
  }

  /** Build slots up to the letter-adjusted capacity (max 9 on this stage). */
  private syncSlotCount(): void {
    // Cap at the dollhouse slot table too — a future capacity letter must
    // degrade to "no new bed drawn", never throw (leg-1 finding, v0.9).
    const cap = Math.min(
      this.dh ? this.dh.slots.length : 9,
      9,
      effectiveCapacity(runtime.state, this.tierId),
    );
    while (this.slots.length < cap) {
      this.slots.push(this.buildSlot(this.slots.length));
    }
  }

  private slotPos(bed: number): { x: number; y: number } {
    if (this.dh) {
      const slot = this.dh.slots[bed];
      if (!slot) throw new Error(`slotPos: no dollhouse bed ${bed} (max ${this.dh.slots.length - 1})`);
      return { x: slot.x, y: slot.y };
    }
    const row = Math.floor(bed / COLS);
    const col = bed % COLS;
    return { x: COL_XS[col]!, y: LAYOUT.floorY - row * ROW_DY };
  }

  /** The current viewport's center in WORLD coords — the anchor for beats with
   *  NO actor (loot pickup, an incoming arrival, the raid alarm) so they fire
   *  over what the player is looking at instead of drifting to the top of the
   *  pannable stage (v0.15.2 fix). */
  private viewCenter(): { x: number; y: number } {
    const v = this.cameras.main.worldView;
    return { x: v.centerX, y: v.centerY };
  }

  /** The bed position a patient currently occupies — captured BEFORE `despawn`
   *  clears the slot, so graduation / promotion / relapse FX land on the actual
   *  patient (v0.15.2). Null if the patient isn't in a slot here. */
  private patientPos(patientId: number): { x: number; y: number } | null {
    for (const view of this.slots) {
      if (view.patientId === patientId) return { x: view.x, y: view.y };
    }
    return null;
  }

  /** A celebration/notification point ABOVE a patient, `lift` px up from their
   *  feet — but clamped to stay within the current viewport. Detox cots sit near
   *  the world top, so a raw lift flies off-screen there; the clamp keeps the FX
   *  visible in every scene while still floating above the patient where there's
   *  room (v0.15.2 playtest: "bigger, above them, and Detox showed nothing"). */
  private fxAbove(pos: { x: number; y: number }, lift: number): { x: number; y: number } {
    const top = this.cameras.main.worldView.top;
    return { x: pos.x, y: Math.max(top + 150, pos.y - lift) };
  }

  private dhWorldH(): number {
    if (!this.dh) return GAME.height;
    return this.dh.worldTop + this.dh.panels.reduce((a, [, h]) => a + h, 0) + this.dh.dirtH;
  }

  private slotScale(bed: number): number {
    return this.dh ? (this.dh.slots[bed]?.scale ?? 1) : 1;
  }

  private drawStage(): void {
    const cfg = TIERS[this.tierId];
    const floor = LAYOUT.floorY;
    if (this.dh) {
      // v0.9 dollhouse: this tier's floors stacked into a pannable world,
      // straight from its DOLLHOUSE config (per-tier data, shared machinery).
      const worldH = this.dhWorldH();
      let bottom = this.dh.worldTop;
      for (const [key, h] of this.dh.panels) {
        bottom += h;
        this.add
          .image(GAME.width / 2, bottom, artTextureKey(key))
          .setOrigin(0.5, 1)
          .setDisplaySize(GAME.width, h)
          .setDepth(-100);
      }
      this.add
        .rectangle(GAME.width / 2, worldH, GAME.width, this.dh.dirtH, 0x3a2e22)
        .setOrigin(0.5, 1)
        .setDepth(-95);
      wirePan(this, worldH);
      this.cameras.main.scrollY = this.dh.parkScroll;
    }
    if (this.dollhouse && this.tierId === "detox") {
      // ── detox one-offs: sub-level shade, IV decor, banners, signage, taps,
      // the med-window lobbyist ─────────────────────────────────────────────
      // Sub-level stays DARK until the permit's beds exist (capacity > 6).
      this.subShade = this.add
        .rectangle(GAME.width / 2, DETOX_SUB_TOP + DETOX_PANEL_SUB_H, GAME.width, DETOX_PANEL_SUB_H, PALETTE.charcoal)
        .setOrigin(0.5, 1)
        .setDepth(-50)
        .setAlpha(0.82);
      // Ambient IV stands flanking the ward (decor; slots carry the gameplay).
      if (isArtLoaded("detox-iv")) {
        for (const [ix, iy, sc] of [[0.12, 0.86, 1.0], [0.55, 0.68, 0.86]] as const) {
          const iv = this.add
            .image(ix * GAME.width, DETOX_WARD_TOP + iy * DETOX_PANEL_WARD_H, artTextureKey("detox-iv"))
            .setOrigin(0.5, 1);
          iv.setScale(sc).setDepth(DETOX_WARD_TOP + iy * DETOX_PANEL_WARD_H - 2);
        }
      }
      // The ward banner's gag — live type over the shell's blank banner
      // (signage policy: text is in-engine, never baked). Geometry MEASURED
      // off the shell: banner spans x 0.26-0.74, y 0.08-0.23 of the panel.
      const bannerCX = GAME.width * 0.5;
      const bannerW = GAME.width * 0.44; // inner width, inside the sag
      const title = this.add
        .text(bannerCX, DETOX_WARD_TOP + Math.round(0.140 * DETOX_PANEL_WARD_H), "NATURAL DETOX\nCERTIFIED*", {
          fontFamily: "Georgia, serif",
          fontSize: "64px",
          fontStyle: "bold",
          color: PALETTE_HEX.charcoal,
          align: "center",
          lineSpacing: 6,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (title.width > bannerW) title.setScale(bannerW / title.width);
      const fine = this.add
        .text(bannerCX, DETOX_WARD_TOP + Math.round(0.213 * DETOX_PANEL_WARD_H), "(*pending review)", {
          fontFamily: "Georgia, serif",
          fontSize: "30px",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (fine.width > bannerW * 0.7) fine.setScale((bannerW * 0.7) / fine.width);
      // Live signage: DAYS WITHOUT INCIDENT (ward wall) + NOW SERVING board
      // + the dispenser's next ticket (med floor). Signage policy: in-engine.
      this.add
        .text(GAME.width * 0.78, DETOX_WARD_TOP + Math.round(0.25 * DETOX_PANEL_WARD_H), "DAYS WITHOUT\nINCIDENT\n3", {
          fontFamily: "Georgia, serif", fontSize: "26px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal, align: "center",
          backgroundColor: PALETTE_HEX.paper, padding: { x: 12, y: 8 },
        })
        .setOrigin(0.5)
        .setDepth(-40);
      // Med-bay banner gag — on the big cloth (measured: center 0.505, 0.19).
      const medTitle = this.add
        .text(GAME.width * 0.505, DETOX_TOP + Math.round(0.165 * DETOX_PANEL_MED_H), "RELIEF IS COMING*", {
          fontFamily: "Georgia, serif",
          fontSize: "56px",
          fontStyle: "bold",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (medTitle.width > GAME.width * 0.46) medTitle.setScale((GAME.width * 0.46) / medTitle.width);
      this.add
        .text(GAME.width * 0.505, DETOX_TOP + Math.round(0.238 * DETOX_PANEL_MED_H), "(*estimated)", {
          fontFamily: "Georgia, serif",
          fontSize: "28px",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      this.add
        .text(GAME.width * 0.505, DETOX_TOP + Math.round(0.315 * DETOX_PANEL_MED_H), "NOW SERVING: 3", {
          fontFamily: "monospace", fontSize: "34px", fontStyle: "bold", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      this.add
        .text(GAME.width * 0.242, DETOX_TOP + Math.round(0.55 * DETOX_PANEL_MED_H), "47", {
          fontFamily: "monospace", fontSize: "28px", fontStyle: "bold", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      // Curated taps (ruling 8): the incident sign + the PAINTED-OVER exit —
      // an invisible region on the right wall; finding it IS the gag.
      const tapRegions: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [
        { x0: 0.68 * GAME.width, y0: DETOX_WARD_TOP + 0.17 * DETOX_PANEL_WARD_H,
          x1: 0.88 * GAME.width, y1: DETOX_WARD_TOP + 0.33 * DETOX_PANEL_WARD_H, key: "detox-incident-sign" },
        { x0: 0.87 * GAME.width, y0: DETOX_WARD_TOP + 0.52 * DETOX_PANEL_WARD_H,
          x1: 0.99 * GAME.width, y1: DETOX_WARD_TOP + 0.86 * DETOX_PANEL_WARD_H, key: "detox-painted-exit" },
      ];
      const onStageTap = (wx: number, wy: number): void => {
        for (const r of tapRegions) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onStageTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onStageTap);
      });
      this.spawnLobbyist();
    }
    if (this.dollhouse && this.tierId === "residential") {
      // Flip-chart gag — live type on the blank easel board (measured ~0.53/0.345).
      // The silly drawing (Russell): a crayon progress chart, tangling —
      // the doodle alone IS the flip-chart's content (text removed).
      if (isArtLoaded("resi-doodle")) {
        const dd = this.add
          .image(GAME.width * 0.53, RESI_GROUP_TOP + Math.round(0.40 * RESI_GROUP_H), artTextureKey("resi-doodle"))
          .setOrigin(0.5, 1)
          .setDepth(-40);
        dd.setScale(Math.min(1.7, 220 / dd.width));
      }
      // Group-room banner (the big cloth): the sharing gag.
      const shareTitle = this.add
        .text(GAME.width * 0.5, RESI_GROUP_TOP + Math.round(0.115 * RESI_GROUP_H), "SHARING IS HEALING*", {
          fontFamily: "Georgia, serif", fontSize: "52px", fontStyle: "bold", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (shareTitle.width > GAME.width * 0.42) shareTitle.setScale((GAME.width * 0.42) / shareTitle.width);
      this.add
        .text(GAME.width * 0.5, RESI_GROUP_TOP + Math.round(0.175 * RESI_GROUP_H), "(*billable as group therapy)", {
          fontFamily: "Georgia, serif", fontSize: "26px", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      // Break-room TV shows what the wellness is for (light text on the
      // dark screen — no cloth in this art; the TV is the sign).
      const breakTitle = this.add
        .text(GAME.width * 0.495, RESI_TOP + Math.round(0.205 * RESI_BREAK_H), "EMPLOYEE\nWELLNESS\nAREA", {
          fontFamily: "Georgia, serif", fontSize: "26px", fontStyle: "bold",
          color: PALETTE_HEX.paper, align: "center", lineSpacing: 2,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (breakTitle.width > 190) breakTitle.setScale(190 / breakTitle.width);
      const breakFine = this.add
        .text(GAME.width * 0.495, RESI_TOP + Math.round(0.264 * RESI_BREAK_H), "(patients: see cafeteria)", {
          fontFamily: "Georgia, serif", fontSize: "16px", color: PALETTE_HEX.paper,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (breakFine.width > 190) breakFine.setScale(190 / breakFine.width);
      // ── the residential prop set (each one a tap gag) ────────────────
      // (world y anchors: break floor ~0.9 BREAK_H; group wall band ~0.45
      // GROUP_H, group floor ~0.85; cafe floor 0.88 CAFE_H)
      const propSpots: Array<[string, number, number]> = [
        // group room
        ["resi-bulletin", 0.16 * GAME.width, RESI_GROUP_TOP + Math.round(0.36 * RESI_GROUP_H)],
        ["resi-payphone", 0.9 * GAME.width, RESI_GROUP_TOP + Math.round(0.38 * RESI_GROUP_H)],
        ["resi-extinguisher", 0.075 * GAME.width, RESI_GROUP_TOP + Math.round(0.62 * RESI_GROUP_H)],
        // cafeteria
        ["resi-urn", 0.13 * GAME.width, RESI_CAFE_TOP + Math.round(0.86 * RESI_CAFE_H)],
        ["resi-caddy", 0.85 * GAME.width, RESI_CAFE_TOP + Math.round(0.8 * RESI_CAFE_H)],
        // break room
        ["resi-microwave", 0.5 * GAME.width, RESI_TOP + Math.round(0.53 * RESI_BREAK_H)],
        ["resi-dartboard", 0.28 * GAME.width, RESI_TOP + Math.round(0.3 * RESI_BREAK_H)],
      ];
      const tappables: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [];
      for (const [key, px, py] of propSpots) {
        if (!isArtLoaded(key)) continue;
        const img = this.add.image(px, py, artTextureKey(key)).setOrigin(0.5, 1).setDepth(py + 4);
        const bb = img.getBounds();
        tappables.push({ x0: bb.left, y0: bb.top, x1: bb.right, y1: bb.bottom, key });
      }
      // baked fixtures get tap regions too (measured off the shells)
      tappables.push(
        { x0: 0.62 * GAME.width, y0: RESI_TOP + 0.28 * RESI_BREAK_H, x1: 0.8 * GAME.width, y1: RESI_TOP + 0.72 * RESI_BREAK_H, key: "resi-vending" },
        { x0: 0.5 * GAME.width, y0: RESI_TOP + 0.42 * RESI_BREAK_H, x1: 0.62 * GAME.width, y1: RESI_TOP + 0.62 * RESI_BREAK_H, key: "resi-espresso" },
        { x0: 0.06 * GAME.width, y0: RESI_TOP + 0.5 * RESI_BREAK_H, x1: 0.3 * GAME.width, y1: RESI_TOP + 0.95 * RESI_BREAK_H, key: "resi-sofa" },
        { x0: 0.68 * GAME.width, y0: RESI_GROUP_TOP + 0.36 * RESI_GROUP_H, x1: 0.78 * GAME.width, y1: RESI_GROUP_TOP + 0.52 * RESI_GROUP_H, key: "resi-tissues" },
      );
      const onResiTap = (wx: number, wy: number): void => {
        for (const r of tappables) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onResiTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onResiTap);
      });
      // Break-room vignette: a different staff scene each visit (visual RNG).
      this.spawnBreakVignette();
      // Cafeteria: the serving station is a PROP so the server works BEHIND
      // it (walk-behind staging), like every interactable counter.
      if (isArtLoaded("resi-station")) {
        const stY = RESI_CAFE_TOP + Math.round(0.88 * RESI_CAFE_H);
        if (isArtLoaded("person-server")) {
          const servers = [0.36, 0.6].map((sx) => {
            const server = new Character(this, GAME.width * sx, stY - 110, "person-server");
            server.setDepth(stY - 20);
            return server;
          });
          // The specials board writes itself: serve it before it turns.
          let line = 0;
          const confer = (): void => {
            const who = servers[line % 2];
            if (!who?.active) return;
            this.floatText(who.x, stY - 470, SERVER_SPECIALS[line % SERVER_SPECIALS.length]!, PALETTE_HEX.charcoal, 2000);
            line += 1;
            this.time.delayedCall(Phaser.Math.Between(4500, 7000), confer);
          };
          this.time.delayedCall(3000, confer);
        }
        const st = this.add
          .image(GAME.width * 0.5, stY, artTextureKey("resi-station"))
          .setOrigin(0.5, 1)
          .setDepth(stY + 10);
        st.setScale(Math.min(1.6, (GAME.width * 0.72) / st.width));
      }
      // Cafeteria menu board.
      this.add
        .text(GAME.width * 0.5, RESI_CAFE_TOP + Math.round(0.24 * RESI_CAFE_H), "MENU\nMEALS: INCLUDED*\n*in your invoice", {
          fontFamily: "monospace", fontSize: "26px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal, align: "center", lineSpacing: 8,
        })
        .setOrigin(0.5)
        .setDepth(-40);
    }
    if (this.dollhouse && this.tierId === "php") {
      // ── PHP one-offs: classroom board + stool, the case-management set.
      // The Buggy floor is the approved frozen tableau — the panel IS the
      // scene, nothing placed on it. Gag copy/taps land the next pass.
      // Classroom back wall: the whiteboard (prop, so its text is live type).
      if (isArtLoaded("php-whiteboard")) {
        const wbY = PHP_CLASS_TOP + Math.round(0.38 * PHP_CLASS_H);
        const wb = this.add
          .image(GAME.width * 0.48, wbY, artTextureKey("php-whiteboard"))
          .setOrigin(0.5, 1)
          .setDepth(wbY - 40);
        wb.setScale(1.5);
        this.phpBoard = wb;
        this.add
          .text(GAME.width * 0.48, wbY - wb.displayHeight * 0.55, "TODAY: coping\nTOMORROW: co-pays", {
            fontFamily: "Georgia, serif", fontSize: "34px", fontStyle: "bold",
            color: PALETTE_HEX.charcoal, align: "center", lineSpacing: 8,
          })
          .setOrigin(0.5)
          .setDepth(wbY - 39);
      }
      if (isArtLoaded("php-stool")) {
        const stY = PHP_CLASS_TOP + Math.round(0.57 * PHP_CLASS_H);
        this.phpStool = this.add
          .image(GAME.width * 0.72, stY, artTextureKey("php-stool"))
          .setOrigin(0.5, 1)
          .setDepth(stY);
      }
      // ── the PHP tap-gag layer: placed props get live bounds; baked
      // tableau features get MEASURED regions (the residential pattern).
      const phpTaps: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [];
      if (this.phpBoard) {
        const bb = this.phpBoard.getBounds();
        phpTaps.push({ x0: bb.left, y0: bb.top, x1: bb.right, y1: bb.bottom, key: "php-board" });
      }
      if (this.phpStool) {
        const bb = this.phpStool.getBounds();
        phpTaps.push({ x0: bb.left, y0: bb.top, x1: bb.right, y1: bb.bottom, key: "php-stool" });
      }
      phpTaps.push(
        // case-management tableau (fractions of the case panel)
        { x0: 0.06 * GAME.width, y0: PHP_TOP + 0.1 * PHP_CASE_H, x1: 0.3 * GAME.width, y1: PHP_TOP + 0.42 * PHP_CASE_H, key: "php-posters" },
        { x0: 0.62 * GAME.width, y0: PHP_TOP + 0.16 * PHP_CASE_H, x1: 0.74 * GAME.width, y1: PHP_TOP + 0.34 * PHP_CASE_H, key: "php-stamps" },
        { x0: 0.72 * GAME.width, y0: PHP_TOP + 0.36 * PHP_CASE_H, x1: 0.95 * GAME.width, y1: PHP_TOP + 0.62 * PHP_CASE_H, key: "php-trays" },
        // classroom entry door = the attendance sheet
        { x0: 0.18 * GAME.width, y0: PHP_CLASS_TOP + 0.17 * PHP_CLASS_H, x1: 0.32 * GAME.width, y1: PHP_CLASS_TOP + 0.37 * PHP_CLASS_H, key: "php-attendance" },
        // buggy tableau
        { x0: 0.2 * GAME.width, y0: PHP_BUGGY_TOP + 0.06 * PHP_BUGGY_H, x1: 0.46 * GAME.width, y1: PHP_BUGGY_TOP + 0.16 * PHP_BUGGY_H, key: "php-climate" },
        { x0: 0.6 * GAME.width, y0: PHP_BUGGY_TOP + 0.2 * PHP_BUGGY_H, x1: 0.86 * GAME.width, y1: PHP_BUGGY_TOP + 0.4 * PHP_BUGGY_H, key: "php-route" },
        { x0: 0.78 * GAME.width, y0: PHP_BUGGY_TOP + 0.42 * PHP_BUGGY_H, x1: 0.93 * GAME.width, y1: PHP_BUGGY_TOP + 0.62 * PHP_BUGGY_H, key: "php-vandoor" },
      );
      const onPhpTap = (wx: number, wy: number): void => {
        for (const r of phpTaps) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onPhpTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onPhpTap);
      });
      // The Buggy's bicker loop — radio arguments + crude road humor over
      // the frozen tableau (visual RNG; riders are baked anchors).
      this.spawnBuggyBicker();
      this.spawnCaseRedTape();
      this.spawnStudentQuestions();
    }
    if (this.dollhouse && this.tierId === "iop") {
      // ── IOP one-offs: live signage (in-engine text policy). The lot's
      // pole sign — STAFF PARKING, closest to the door, naturally.
      const iopTaps: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [
        // check-in floor (fractions measured off the board)
        { x0: 0.42 * GAME.width, y0: IOP_TOP + 0.2 * IOP_CHECK_H, x1: 0.64 * GAME.width, y1: IOP_TOP + 0.36 * IOP_CHECK_H, key: "iop-cups" },
        { x0: 0.28 * GAME.width, y0: IOP_TOP + 0.18 * IOP_CHECK_H, x1: 0.4 * GAME.width, y1: IOP_TOP + 0.34 * IOP_CHECK_H, key: "iop-hatch" },
        { x0: 0.3 * GAME.width, y0: IOP_TOP + 0.36 * IOP_CHECK_H, x1: 0.42 * GAME.width, y1: IOP_TOP + 0.52 * IOP_CHECK_H, key: "iop-clipboard" },
        { x0: 0.7 * GAME.width, y0: IOP_TOP + 0.12 * IOP_CHECK_H, x1: 0.88 * GAME.width, y1: IOP_TOP + 0.72 * IOP_CHECK_H, key: "iop-restroom" },
        // group floor
        { x0: 0.12 * GAME.width, y0: IOP_GROUP_TOP + 0.3 * IOP_GROUP_H, x1: 0.31 * GAME.width, y1: IOP_GROUP_TOP + 0.78 * IOP_GROUP_H, key: "iop-easel" },
        { x0: 0.77 * GAME.width, y0: IOP_GROUP_TOP + 0.52 * IOP_GROUP_H, x1: 0.98 * GAME.width, y1: IOP_GROUP_TOP + 0.82 * IOP_GROUP_H, key: "iop-thermos" },
        // the lot
        { x0: 0.67 * GAME.width, y0: IOP_LOT_TOP + 0.37 * IOP_LOT_H, x1: 0.81 * GAME.width, y1: IOP_LOT_TOP + 0.52 * IOP_LOT_H, key: "iop-staffsign" },
      ];
      const onIopTap = (wx: number, wy: number): void => {
        for (const r of iopTaps) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onIopTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onIopTap);
      });
      // After-work sighs from whoever's actually seated.
      this.spawnGroupSighs();
      // The guy in the teal car, drafting his excuses.
      this.spawnLotExcuses();
      // The sign is DRAWN, not baked — Gemini kept eating the plate, and
      // in-engine means it's exactly as big as Russell wants it.
      const signCX = GAME.width * 0.741;
      const signY = IOP_LOT_TOP + Math.round(0.42 * IOP_LOT_H);
      this.add
        .rectangle(signCX, signY, 260, 170, PALETTE.paper)
        .setStrokeStyle(5, PALETTE.charcoal)
        .setDepth(-41);
      this.add
        .text(signCX, signY - 18, "STAFF\nPARKING", {
          fontFamily: "Georgia, serif", fontSize: "38px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal, align: "center", lineSpacing: 2,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      this.add
        .text(signCX, signY + 58, "(closest to door)", {
          fontFamily: "Georgia, serif", fontSize: "17px",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
    }
    if (this.dollhouse && this.tierId === "soberLiving") {
      // ── SOBER LIVING one-offs: the table prop (occludes far-side
      // sitters), RENT DUE on the corkboard, taps, and three speech lanes.
      {
        // The table is DRAWN (the lot-sign rule): three prop derivations
        // came back squarish; in-engine is exact and forever tunable.
        const { x: tx, y: ty, w: tw, h: th } = SOBER_TABLE;
        const topH = Math.round(th * 0.38);
        const legW = 44;
        const legH = th - topH;
        const d = ty;
        // back legs (peek behind the top, slightly inset + raised)
        this.add.rectangle(tx - tw * 0.22, ty - Math.round(legH * 0.3), legW - 10, Math.round(legH * 0.7), 0x4a3f35)
          .setOrigin(0.5, 1).setDepth(d - 1);
        this.add.rectangle(tx + tw * 0.22, ty - Math.round(legH * 0.3), legW - 10, Math.round(legH * 0.7), 0x4a3f35)
          .setOrigin(0.5, 1).setDepth(d - 1);
        // front legs — inboard so the end sitters don't hide them
        this.add.rectangle(tx - tw * 0.30, ty, legW, legH, PALETTE.charcoal)
          .setOrigin(0.5, 1).setDepth(d);
        this.add.rectangle(tx + tw * 0.30, ty, legW, legH, PALETTE.charcoal)
          .setOrigin(0.5, 1).setDepth(d);
        // the top slab
        this.add.rectangle(tx, ty - legH, tw, topH, 0xf7f3e8)
          .setStrokeStyle(12, PALETTE.charcoal)
          .setOrigin(0.5, 1)
          .setDepth(d + 1);
      }
      this.add
        .text(GAME.width * 0.365, SOBER_KITCH_TOP + Math.round(0.475 * SOBER_KITCH_H), "RENT\nDUE", {
          fontFamily: "Georgia, serif", fontSize: "26px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal, align: "center", lineSpacing: 0,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      const soberTaps: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [
        // bedroom tableau
        { x0: 0.38 * GAME.width, y0: SOBER_TOP + 0.12 * SOBER_BED_H, x1: 0.62 * GAME.width, y1: SOBER_TOP + 0.42 * SOBER_BED_H, key: "sober-fan" },
        { x0: 0.72 * GAME.width, y0: SOBER_TOP + 0.55 * SOBER_BED_H, x1: 0.95 * GAME.width, y1: SOBER_TOP + 0.95 * SOBER_BED_H, key: "sober-hamper" },
        // kitchen
        { x0: 0.6 * GAME.width, y0: SOBER_KITCH_TOP + 0.25 * SOBER_KITCH_H, x1: 0.82 * GAME.width, y1: SOBER_KITCH_TOP + 0.48 * SOBER_KITCH_H, key: "sober-chorechart" },
        { x0: 0.55 * GAME.width, y0: SOBER_KITCH_TOP + 0.1 * SOBER_KITCH_H, x1: 0.8 * GAME.width, y1: SOBER_KITCH_TOP + 0.24 * SOBER_KITCH_H, key: "sober-milk" },
        { x0: 0.22 * GAME.width, y0: SOBER_KITCH_TOP + 0.26 * SOBER_KITCH_H, x1: 0.44 * GAME.width, y1: SOBER_KITCH_TOP + 0.4 * SOBER_KITCH_H, key: "sober-corkboard" },
        { x0: 0.83 * GAME.width, y0: SOBER_KITCH_TOP + 0.27 * SOBER_KITCH_H, x1: 0.98 * GAME.width, y1: SOBER_KITCH_TOP + 0.41 * SOBER_KITCH_H, key: "sober-coffeemaker" },
        { x0: 0.83 * GAME.width, y0: SOBER_KITCH_TOP + 0.43 * SOBER_KITCH_H, x1: 0.99 * GAME.width, y1: SOBER_KITCH_TOP + 0.58 * SOBER_KITCH_H, key: "sober-stove" },
        // bathroom tableau
        { x0: 0.6 * GAME.width, y0: SOBER_BATH_TOP + 0.5 * SOBER_BATH_H, x1: 0.85 * GAME.width, y1: SOBER_BATH_TOP + 0.85 * SOBER_BATH_H, key: "sober-caddy" },
        { x0: 0.66 * GAME.width, y0: SOBER_BATH_TOP + 0.1 * SOBER_BATH_H, x1: 0.8 * GAME.width, y1: SOBER_BATH_TOP + 0.38 * SOBER_BATH_H, key: "sober-mirrorpage" },
      ];
      const onSoberTap = (wx: number, wy: number): void => {
        for (const r of soberTaps) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onSoberTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onSoberTap);
      });
      this.spawnRoommates();
      this.spawnChoreGrumbles();
    }
    if (!this.dh) {
      this.add.rectangle(GAME.width / 2, floor + 6, GAME.width, 12, PALETTE.charcoal);
      this.add.rectangle(GAME.width / 2, floor + 370, GAME.width, 720, PALETTE.paper).setDepth(-10);
    }
    // Shared hero banner (band + sunburst + costumed owner + tier logotype +
    // gag pill + facility nameplate). Replaces the old plain title/gag text.
    const slug = HERO_SLUG[this.tierId];
    this.facilityBar = buildHero(this, {
      titleKey: `hero-${slug}`,
      titleText: cfg.title,
      ownerKey: `owner-${slug}`,
      motto: HERO_MOTTO[this.tierId],
      theme: HERO_THEME[slug]!,
    });
    // M8 transparency panel — the player watches themselves buy worse outcomes.
    // Sits just below the hero nameplate.
    this.riskPanel = this.add
      .text(GAME.width / 2, HERO.y + HERO.h + 92, "", {
        fontFamily: "monospace",
        fontSize: "26px",
        color: PALETTE_HEX.charcoal,
        align: "center",
        backgroundColor: PALETTE_HEX.paper,
        padding: { x: 18, y: 10 },
      })
      .setOrigin(0.5, 0)
      // Screen-pinned chrome like the hero above it — the M8 transparency
      // panel must survive the parked camera (Codex leg-4 finding, PR #20).
      .setScrollFactor(0)
      .setDepth(900);
  }

  private buildSlot(bed: number): SlotView {
    const { x, y } = this.slotPos(bed);
    const scale = this.slotScale(bed);
    const emptyTex = this.dh?.slots[bed]?.emptyKey ?? this.dh?.slotEmptyKey ?? "";
    const occupiedTex = this.dh?.slots[bed]?.occupiedKey ?? this.dh?.slotOccupiedKey ?? "";

    let cot: Phaser.GameObjects.Image | undefined;
    let tapTarget: Phaser.GameObjects.GameObject;
    if (this.dollhouse) {
      // The bed IS the cot cut (empty <-> occupied texture swap on state).
      cot = this.add
        .image(x, y, artTextureKey(emptyTex))
        .setFlipX(this.dh!.slots[bed]?.flip ?? false)
        .setOrigin(0.5, 1)
        .setScale(scale)
        .setDepth(y + 4)
        .setInteractive({ useHandCursor: true });
      tapTarget = cot;
    } else {
      const frame = this.add
        .rectangle(x, y, SLOT_W, SLOT_H, PALETTE[TIERS[this.tierId].accent])
        .setOrigin(0.5, 1)
        .setStrokeStyle(8, PALETTE.charcoal)
        .setDepth(y + 4)
        .setInteractive({ useHandCursor: true });
      this.add
        .rectangle(x, y - SLOT_H + 28, SLOT_W - 44, 40, PALETTE.paper)
        .setStrokeStyle(6, PALETTE.charcoal)
        .setDepth(y + 5);
      tapTarget = frame;
    }

    // The bar sits ~540px above its own bed — i.e. in the row above's space — so
    // it must depth ABOVE every bed frame (which are at y+4), or the higher bed
    // covers it. Default depth 0 put it behind all beds.
    const barY = this.dollhouse ? y - Math.round(250 * scale) : y - 540;
    const barFrame = this.add
      .rectangle(x, barY, 230, 28, PALETTE.paper)
      .setStrokeStyle(6, PALETTE.charcoal)
      .setDepth(y + 50)
      .setVisible(false);
    const barFill = this.add
      .rectangle(x - 110, barY, 0, 16, PALETTE.green)
      .setOrigin(0, 0.5)
      .setDepth(y + 51)
      .setVisible(false);

    const chipBg = this.add.rectangle(0, 0, 190, 70, PALETTE.coralRed).setStrokeStyle(7, PALETTE.charcoal);
    const chipLabel = this.add
      .text(0, 0, "TREAT", {
        fontFamily: "Georgia, serif",
        fontSize: "36px",
        fontStyle: "bold",
        color: PALETTE_HEX.paper,
      })
      .setOrigin(0.5);
    const treatChip = this.add
      .container(x, this.dollhouse ? barY - 84 : y - 630, [chipBg, chipLabel])
      .setVisible(false)
      .setDepth(y + 30)
      .setSize(190, 70)
      .setInteractive({ useHandCursor: true });

    tapTarget.on("pointerup", (p: Phaser.Input.Pointer) => {
      if (p.getDistance() > 14) return;
      this.onTend(bed, x, y);
    });
    treatChip.on("pointerup", (p: Phaser.Input.Pointer) => {
      if (p.getDistance() > 14) return;
      this.onTreat(bed, x, y);
    });

    return { x, y, cot, scale, emptyTex, occupiedTex, sprite: undefined, patientId: undefined, lastState: undefined, barFrame, barFill, treatChip };
  }

  private onTend(bed: number, x: number, y: number): void {
    const p = patientInSlot(runtime.state, this.tierId, bed);
    if (!p) {
      this.floatText(x, y - 300, "empty", PALETTE_HEX.gray);
      return;
    }
    try {
      tendSlot(runtime.state, this.tierId, bed);
      this.floatText(x, y - 300, `${TIERS[this.tierId].manualVerb} ✚`, PALETTE_HEX.teal);
      runtime.act([]);
    } catch {
      this.floatText(x, y - 300, "needs TREATMENT!", PALETTE_HEX.coralRed);
    }
    this.reconcile();
  }

  private onTreat(bed: number, x: number, y: number): void {
    try {
      treatSlot(runtime.state, this.tierId, bed);
      this.floatText(x, y - 300, "treated ✚", PALETTE_HEX.green);
      runtime.act([]);
    } catch {
      this.floatText(x, y - 300, "nothing to treat", PALETTE_HEX.gray);
    }
    this.reconcile();
  }

  /** Reconcile the loot sprite with the save (create + wake). */
  private syncLootFromState(): void {
    const mine = runtime.state.run.events.active.find((l) => l.tier === this.tierId);
    if (this.lootSprite && (!mine || mine.id !== this.lootSprite.id)) {
      this.removeLootSprite(this.lootSprite.id, false);
    }
    if (mine && (!this.lootSprite || this.lootSprite.id !== mine.id)) {
      this.spawnLootSprite(mine.id, mine.kind, mine.item);
    }
  }

  /** v0.13 loot: simple palette draws (RULED §8.3 launch art) at a fixed
   *  per-tier anchor; pulse while live, fade handled by expiry event. */
  private spawnLootSprite(id: number, kind: "money" | "contraband", item: string): void {
    if (this.lootSprite) this.removeLootSprite(this.lootSprite.id, false);
    const g = this.add.graphics();
    const money = kind === "money";
    const body = money ? 0x6fa85b : 0xe2664e;
    g.fillStyle(0x221f1a, 1).fillRoundedRect(-46, -34, 92, 68, 14);
    g.fillStyle(body, 1).fillRoundedRect(-40, -28, 80, 56, 10);
    g.fillStyle(0xf4ecd8, 1).fillCircle(0, 0, 14);
    const label = this.add
      .text(0, 52, item.toUpperCase(), { fontFamily: "sans-serif", fontSize: "24px", color: "#221f1a", fontStyle: "bold" })
      .setOrigin(0.5, 0);
    // CAMERA-PINNED (v0.13 review, recorded deviation from the RFC's
    // in-world anchors): every tier parks its camera differently and a
    // world anchor can land behind pinned chrome or under tableau art —
    // a buried-but-tappable pickup is exactly what the LEGIBILITY LAW
    // forbids. Fixed viewport slot instead: right side, below the hero
    // zone, above the action tiles. Same v0.9 lesson as the risk panel.
    const anchor = { x: GAME.width - 220, y: 1680 };
    const box = this.add.container(anchor.x, anchor.y, [g, label]).setDepth(4000).setSize(120, 120); // above the y-sorted world (~2500-2900), below pinned chrome (9000+)
    box.setScrollFactor(0);
    // scrollFactor(0) renders at screen coords but Phaser hit-tests in
    // WORLD coords (v0.13 review find) — so the tap is a scene-level
    // SCREEN-space check against the pinned slot instead.
    const onTap = (ptr: Phaser.Input.Pointer): void => {
      if (Math.abs(ptr.x - anchor.x) > 70 || ptr.y < anchor.y - 70 || ptr.y > anchor.y + 90) return;
      try {
        runtime.act(collectLoot(runtime.state, id));
      } catch (err) {
        console.warn("loot tap raced its own expiry:", err); // legal: it despawned this tick
      }
    };
    this.input.on("pointerdown", onTap);
    box.once("destroy", () => this.input?.off("pointerdown", onTap));
    this.tweens.add({ targets: box, scale: { from: 1, to: 1.12 }, yoyo: true, repeat: -1, duration: 480 });
    this.lootSprite = { id, obj: box };
  }

  private removeLootSprite(id: number, collected: boolean): void {
    if (!this.lootSprite || this.lootSprite.id !== id) return;
    const obj = this.lootSprite.obj;
    this.lootSprite = undefined;
    this.tweens.add({
      targets: obj,
      alpha: 0,
      scale: collected ? 1.6 : 0.6,
      duration: collected ? 220 : 600,
      onComplete: () => obj.destroy(),
    });
  }

  private onEvents(events: TickEvent[]): void {
    if (this.facilityBar) fitFacilityName(this.facilityBar); // pick up a live rename
    for (const ev of events) {
      switch (ev.type) {
        case "lootSpawned": {
          if (ev.tier === this.tierId) this.spawnLootSprite(ev.id, ev.kind, ev.item);
          break;
        }
        case "lootExpired": {
          if (ev.tier === this.tierId) this.removeLootSprite(ev.id, false);
          break;
        }
        case "lootCollected": {
          if (ev.tier === this.tierId) {
            this.removeLootSprite(ev.id, true);
            const msg = ev.kind === "money" ? `+$${ev.funding}` : `fenced +$${ev.funding} · heat +${ev.heat}${ev.sk > 0 ? ` · SK +${ev.sk}` : ""}`;
            const at = this.viewCenter();
            this.floatText(at.x, at.y, msg, ev.kind === "money" ? PALETTE_HEX.green : PALETTE_HEX.coralRed);
          }
          break;
        }
        case "promoted": {
          if (ev.from === this.tierId) {
            const pos = this.patientPos(ev.patientId) ?? this.viewCenter(); // capture BEFORE despawn
            this.despawn(ev.patientId, (s) =>
              s.walkTo(EXIT_RIGHT, s.y, () => s.destroy()),
            );
            const a = this.fxAbove(pos, 470);
            this.floatText(a.x, a.y, `${ev.name} promoted! +$${ev.payout}`, PALETTE_HEX.green, 0, 50);
            cashRain(this, a.x, a.y + 50); // v0.7 juice — a promotion pays, above the patient
          } else if (ev.to === this.tierId) {
            const at = this.viewCenter();
            this.floatText(at.x, at.y, `${ev.name} arrives`, PALETTE_HEX.teal);
          }
          break;
        }
        case "graduated": {
          if (ev.tier === this.tierId) {
            const pos = this.patientPos(ev.patientId) ?? this.viewCenter(); // capture BEFORE despawn
            this.despawn(ev.patientId, (s) => s.celebrateAndExit(EXIT_RIGHT));
            const note = ev.intoAlumni ? "GRADUATED → Alumni!" : "GRADUATED!*";
            const a = this.fxAbove(pos, 520); // well above the patient, clamped on-screen
            this.floatText(a.x, a.y, `${ev.name} ${note} +$${ev.payout}`, PALETTE_HEX.green, 0, 66);
            cashRain(this, a.x, a.y + 50); // the payout rains above the grad
            if (!ev.intoAlumni) {
              this.floatText(a.x, a.y + 88, "*outcome data unavailable", PALETTE_HEX.gray);
            }
          }
          break;
        }
        case "relapsed": {
          const pos = this.patientPos(ev.patientId) ?? this.viewCenter(); // capture BEFORE despawn
          if (ev.from === this.tierId && ev.to !== this.tierId) {
            this.despawn(ev.patientId, (s) => s.walkTo(-140, s.y, () => s.destroy()));
          }
          if (ev.from === this.tierId || ev.to === this.tierId) {
            const msg = ev.severity === "major" ? `${ev.name}: MAJOR relapse` : `${ev.name}: relapse`;
            const a = this.fxAbove(pos, 470);
            this.floatText(a.x, a.y, msg, PALETTE_HEX.coralRed, 0, 50);
          }
          break;
        }
        case "gameOver": {
          // v0.7 juice — the alarm: a screen flash + camera shake; raid adds debris.
          flash(this);
          shake(this);
          if (ev.reason === "raid") {
            const at = this.viewCenter();
            debris(this, at.x, at.y);
          }
          break;
        }
        case "died": {
          if (ev.tier === this.tierId) {
            const view = this.slots[ev.bed];
            this.floatText(this.slotPos(ev.bed).x, this.slotPos(ev.bed).y - 620, "✝", PALETTE_HEX.charcoal);
            flowerBurst(this, this.slotPos(ev.bed).x, this.slotPos(ev.bed).y - 100); // v0.7 juice — the flower
            if (view?.sprite && view.patientId === ev.patientId) {
              const s = view.sprite;
              view.sprite = undefined;
              view.patientId = undefined;
              view.lastState = undefined;
              view.cot?.setTexture(artTextureKey(view.emptyTex));
              s.wilt(() => this.placeFlower(view.x, view.y));
            } else if (view && this.dollhouse && view.patientId === ev.patientId) {
              view.patientId = undefined;
              view.lastState = undefined;
              view.cot?.setTexture(artTextureKey(view.emptyTex));
              this.placeFlower(view.x, view.y);
            }
          }
          break;
        }
        case "upgraded":
          if (ev.what === this.tierId) this.syncStaff();
          break;
        case "tierUnlocked":
          this.installActions(); // frontier moved — rebuild the action bar
          break;
        case "purchasableOffered":
          this.installActions(); // an offer opened — surface the OPEN button
          break;
        case "capacityRaised":
          if (ev.tier === this.tierId) this.syncSlotCount();
          break;
        case "teachControl":
          if (ev.control === "treat") {
            // pulse every visible treat chip until one is used
            for (const v of this.slots) {
              if (v.treatChip.visible) {
                this.tweens.add({ targets: v.treatChip, scale: 1.15, duration: 380, yoyo: true, repeat: 5 });
              }
            }
          }
          break;
        default:
          break;
      }
    }
  }

  private despawn(patientId: number, animate: (s: Character) => void): void {
    for (const view of this.slots) {
      if (view.patientId !== patientId) continue;
      let s = view.sprite;
      if (!s && this.dollhouse) {
        // The occupant is IN the cot — get up, then play the exit animation.
        s = new Character(this, view.x, view.y - 10, resolvePatientTexture(patientId));
        s.setScale(view.scale);
      }
      view.sprite = undefined;
      view.patientId = undefined;
      view.lastState = undefined;
      view.cot?.setTexture(artTextureKey(view.emptyTex));
      if (s) animate(s);
      return;
    }
  }

  private reconcile(): void {
    for (let bed = 0; bed < this.slots.length; bed++) {
      const view = this.slots[bed]!;
      const p = patientInSlot(runtime.state, this.tierId, bed);
      if (p) {
        this.syncPatientView(view, p);
      } else {
        if (view.sprite) {
          view.sprite.destroy();
          view.sprite = undefined;
        }
        view.patientId = undefined;
        view.lastState = undefined;
        view.cot?.setTexture(artTextureKey(view.emptyTex));
        view.barFrame.setVisible(false);
        view.barFill.setVisible(false);
        view.treatChip.setVisible(false);
      }
    }
    // Sub-level lights: dark until the permit's beds exist (capacity > 6).
    this.subShade?.setVisible(effectiveCapacity(runtime.state, this.tierId) <= 6);
    this.syncStaff();
    this.syncRiskPanel();
  }

  private syncPatientView(view: SlotView, p: Patient): void {
    if (this.dollhouse) {
      // Walk-in: a NEW occupant spawns at the ward door, walks the aisle to
      // the cot, then the sprite retires and the cot flips to OCCUPIED.
      if (view.patientId !== p.id) {
        view.sprite?.destroy();
        view.patientId = p.id;
        view.lastState = p.state;
        view.cot?.setTexture(artTextureKey(view.emptyTex));
        const walker = new Character(this, this.dh!.door.x, this.dh!.door.y, resolvePatientTexture(p.id));
        walker.setScale(view.scale);
        view.sprite = walker;
        const claimed = p.id;
        walker.walkTo(view.x, view.y, () => {
          walker.destroy();
          if (view.sprite === walker) view.sprite = undefined;
          // Only occupy if this patient still owns the bed (fast churn guard).
          if (view.patientId === claimed) view.cot?.setTexture(artTextureKey(view.occupiedTex));
        });
      } else if (!view.sprite) {
        view.cot?.setTexture(artTextureKey(view.occupiedTex));
      }
      view.lastState = p.state;
    } else {
      if (!view.sprite || view.patientId !== p.id) {
        view.sprite?.destroy();
        view.sprite = new Character(this, view.x, view.y - 26, "person-new");
        view.patientId = p.id;
        view.lastState = undefined;
      }
      if (view.lastState !== p.state) {
        view.sprite.setPatientState(p.state);
        view.lastState = p.state;
      }
    }
    view.barFrame.setVisible(true);
    view.barFill.setVisible(true).width = 218 * (p.progress / 100);
    view.barFill.fillColor = p.state === "issue" ? PALETTE.coralRed : PALETTE.green;
    view.treatChip.setVisible(p.state === "issue");
  }

  /**
   * The ward NURSE (v0.9, dollhouse): an ambient actor who tends occupied
   * cots — walk to a bedside, pause with a ✚, move on. Appears with the
   * tier's automator (understaffing is a feature: no hire, no nurse).
   * Visual randomness only — never the sim RNG.
   */
  private spawnNurse(): void {
    if (this.tierId !== "detox" || !this.dollhouse || this.nurse || !isArtLoaded("person-nurse")) return;
    const nurse = new Character(this, GAME.width * 0.55, DETOX_WARD_TOP + Math.round(0.8 * DETOX_PANEL_WARD_H), "person-nurse");
    this.nurse = nurse;
    const rounds = (): void => {
      if (!nurse.active) return;
      const occupied = this.slots.filter((v) => v.patientId !== undefined && v.cot);
      if (occupied.length === 0) {
        // nobody to tend — drift to the cooler and wait
        nurse.walkTo(GAME.width * 0.78, DETOX_WARD_TOP + Math.round(0.82 * DETOX_PANEL_WARD_H), () => {
          this.time.delayedCall(Phaser.Math.Between(2500, 5000), rounds);
        });
        return;
      }
      const v = occupied[Phaser.Math.Between(0, occupied.length - 1)]!;
      nurse.setScale(v.scale);
      nurse.walkTo(v.x + Math.round(150 * v.scale), v.y, () => {
        this.floatText(v.x, v.y - Math.round(200 * v.scale), "✚", PALETTE_HEX.teal);
        if (Phaser.Math.Between(1, 100) <= 40) {
          const line = WARD_NURSE_MUTTERS[Phaser.Math.Between(0, WARD_NURSE_MUTTERS.length - 1)]!;
          this.floatText(nurse.x, nurse.y - Math.round(360 * v.scale), line, PALETTE_HEX.charcoal, 1900);
        }
        this.time.delayedCall(Phaser.Math.Between(1400, 3000), rounds);
      });
    };
    rounds();
  }

  /**
   * The med-window LOBBYIST: an ambient patient permanently at the glass,
   * cycling implausible symptoms (MED_WINDOW_SYMPTOMS — data). Same face
   * every session (fixed variant); visual randomness only.
   */
  /** One vignette per visit: place the actors, loop the script's bubbles. */
  private platitudesRunning = false;

  /** The residential facilitator teaches in warped platitudes (data). */
  /** PHP: the benefits maze — red-tape session over the case tableau. */
  private spawnCaseRedTape(): void {
    const script = CASE_RED_TAPE[Math.floor(Math.random() * CASE_RED_TAPE.length)]!;
    let i = 0;
    const speak = (): void => {
      if (!this.scene.isActive()) return;
      const [who, line] = script.lines[i % script.lines.length]!;
      const anchor = CASE_ACTORS[who]!;
      const wx = anchor.x * GAME.width;
      const wy = PHP_TOP + Math.round(anchor.y * PHP_CASE_H);
      if (this.speechOnCamera(wy)) this.floatText(wx, wy, line, PALETTE_HEX.charcoal, 2400);
      i += 1;
      this.time.delayedCall(Phaser.Math.Between(3800, 6200), speak);
    };
    this.time.delayedCall(3600, speak);
  }

  /** PHP: hand-raisers — earnest questions from a random OCCUPIED desk.
   * Legal-empty: with nobody seated it stays quiet and retries later. */
  private spawnStudentQuestions(): void {
    let line = Phaser.Math.Between(0, STUDENT_QUESTIONS.length - 1);
    const raise = (): void => {
      if (!this.scene.isActive()) return;
      const seated = this.slots.filter((v) => v.patientId !== undefined && v.cot);
      const pick = seated[Math.floor(Math.random() * Math.max(1, seated.length))];
      if (pick) {
        this.floatText(pick.x, pick.y - 260 * pick.scale, STUDENT_QUESTIONS[line % STUDENT_QUESTIONS.length]!, PALETTE_HEX.charcoal, 2200);
        line += 1;
      }
      this.time.delayedCall(Phaser.Math.Between(9000, 14000), raise);
    };
    this.time.delayedCall(6000, raise);
  }

  /** IOP: verification theater — the Case Manager recites procedure. */
  private theaterRunning = false;
  private spawnCheckinTheater(): void {
    if (this.tierId !== "iop" || !this.dollhouse || this.theaterRunning) return;
    this.theaterRunning = true;
    let line = Phaser.Math.Between(0, IOP_CHECKIN_LINES.length - 1);
    const recite = (): void => {
      const s = this.staffSprite;
      if (!s?.active) return;
      this.floatText(s.x, s.y - 400, IOP_CHECKIN_LINES[line % IOP_CHECKIN_LINES.length]!, PALETTE_HEX.charcoal, 2400);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(7000, 11000), recite);
    };
    this.time.delayedCall(3000, recite);
  }

  /** IOP: the group facilitator — visible, legible, runs the meeting from
   * the easel (hire-gated with the Case Manager; one staff hire runs the
   * program). */
  private iopFacilitator: Phaser.GameObjects.Image | null = null;
  private facilitatingRunning = false;
  private spawnIopFacilitator(): void {
    if (this.tierId !== "iop" || !this.dollhouse) return;
    if (!this.iopFacilitator && isArtLoaded("person-counselor")) {
      const fy = IOP_GROUP_TOP + Math.round(0.70 * IOP_GROUP_H);
      this.iopFacilitator = this.add
        .image(GAME.width * 0.315, fy, artTextureKey("person-counselor"))
        .setOrigin(0.5, 1)
        .setDepth(fy);
    }
    if (this.facilitatingRunning) return;
    this.facilitatingRunning = true;
    let line = Phaser.Math.Between(0, IOP_FACILITATOR_LINES.length - 1);
    const lead = (): void => {
      const f = this.iopFacilitator;
      if (!f?.active) return;
      this.floatText(f.x, f.y - 400, IOP_FACILITATOR_LINES[line % IOP_FACILITATOR_LINES.length]!, PALETTE_HEX.charcoal, 2400);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(8000, 12000), lead);
    };
    this.time.delayedCall(4500, lead);
  }

  /** IOP: the guy in the car, rehearsing reasons not to go in. Slow
   * cadence — he's alone, and he's not going anywhere. */
  private spawnLotExcuses(): void {
    if (this.tierId !== "iop") return;
    let line = Phaser.Math.Between(0, LOT_EXCUSES.length - 1);
    const excuse = (): void => {
      if (!this.scene.isActive()) return;
      const ey = IOP_LOT_TOP + Math.round(0.52 * IOP_LOT_H);
      if (this.speechOnCamera(ey)) {
        this.floatText(GAME.width * 0.17, ey, LOT_EXCUSES[line % LOT_EXCUSES.length]!, PALETTE_HEX.charcoal, 2600);
      }
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(10000, 15000), excuse);
    };
    this.time.delayedCall(3500, excuse);
  }

  /** Ambient speech is skipped (but keeps rescheduling) while its anchor is
   * outside the camera view — off-screen bubbles are pure texture churn
   * (perf review finding, v0.9). */
  private speechOnCamera(worldY: number): boolean {
    const cam = this.cameras.main;
    return worldY > cam.scrollY - 200 && worldY < cam.scrollY + GAME.height + 200;
  }

  /** Sober Living: the offender and the sufferer, all night. */
  private spawnRoommates(): void {
    if (this.tierId !== "soberLiving") return;
    let i = 0;
    const anchors = [
      { x: 0.25 * GAME.width, y: SOBER_TOP + Math.round(0.45 * SOBER_BED_H) },
      { x: 0.62 * GAME.width, y: SOBER_TOP + Math.round(0.45 * SOBER_BED_H) },
    ];
    const turn = (): void => {
      if (!this.scene.isActive()) return;
      const [who, line] = ROOMMATE_SCRIPT[i % ROOMMATE_SCRIPT.length]!;
      const a = anchors[who]!;
      if (this.speechOnCamera(a.y)) this.floatText(a.x, a.y, line, PALETTE_HEX.charcoal, 2300);
      i += 1;
      this.time.delayedCall(Phaser.Math.Between(4200, 6500), turn);
    };
    this.time.delayedCall(2800, turn);
  }

  /** Sober Living: chore-duty grumbles from the bathroom tableau. */
  private spawnChoreGrumbles(): void {
    if (this.tierId !== "soberLiving") return;
    let line = Phaser.Math.Between(0, CHORE_GRUMBLES.length - 1);
    const grumble = (): void => {
      if (!this.scene.isActive()) return;
      const gy = SOBER_BATH_TOP + Math.round(0.42 * SOBER_BATH_H);
      if (this.speechOnCamera(gy)) {
        this.floatText(GAME.width * 0.42, gy, CHORE_GRUMBLES[line % CHORE_GRUMBLES.length]!, PALETTE_HEX.charcoal, 2400);
      }
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(9000, 13000), grumble);
    };
    this.time.delayedCall(5200, grumble);
  }

  /** Sober Living: the House Manager's landlord meeting (hire-gated). */
  private meetingRunning = false;
  private spawnHouseMeeting(): void {
    if (this.tierId !== "soberLiving" || !this.dollhouse || this.meetingRunning) return;
    this.meetingRunning = true;
    let line = Phaser.Math.Between(0, HOUSE_MEETING_LINES.length - 1);
    const preside = (): void => {
      const s = this.staffSprite;
      if (!s?.active) return;
      this.floatText(s.x, s.y - 400, HOUSE_MEETING_LINES[line % HOUSE_MEETING_LINES.length]!, PALETTE_HEX.charcoal, 2500);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(7500, 11500), preside);
    };
    this.time.delayedCall(3800, preside);
  }

  /** IOP: after-work sighs from a random occupied folding chair. */
  private spawnGroupSighs(): void {
    if (this.tierId !== "iop") return;
    let line = Phaser.Math.Between(0, IOP_GROUP_SIGHS.length - 1);
    const sigh = (): void => {
      if (!this.scene.isActive()) return;
      const seated = this.slots.filter((v) => v.patientId !== undefined && v.cot);
      const pick = seated[Math.floor(Math.random() * Math.max(1, seated.length))];
      if (pick) {
        this.floatText(pick.x, pick.y - 260 * pick.scale, IOP_GROUP_SIGHS[line % IOP_GROUP_SIGHS.length]!, PALETTE_HEX.charcoal, 2200);
        line += 1;
      }
      this.time.delayedCall(Phaser.Math.Between(9000, 14000), sigh);
    };
    this.time.delayedCall(5000, sigh);
  }

  /** PHP: the eternal van argument — one bicker script per visit, looping. */
  private spawnBuggyBicker(): void {
    const script = BUGGY_BICKERS[Math.floor(Math.random() * BUGGY_BICKERS.length)]!;
    let i = 0;
    const speak = (): void => {
      if (!this.scene.isActive()) return;
      const [who, line] = script.lines[i % script.lines.length]!;
      const anchor = BUGGY_RIDERS[who]!;
      const wx = anchor.x * GAME.width;
      const wy = PHP_BUGGY_TOP + Math.round(anchor.y * PHP_BUGGY_H);
      if (this.speechOnCamera(wy)) this.floatText(wx, wy, line, PALETTE_HEX.charcoal, 2400);
      i += 1;
      this.time.delayedCall(Phaser.Math.Between(3800, 6200), speak);
    };
    this.time.delayedCall(2500, speak);
  }

  private spawnPlatitudes(): void {
    if (this.tierId !== "residential" || !this.dollhouse || this.platitudesRunning) return;
    this.platitudesRunning = true;
    let line = Phaser.Math.Between(0, FACILITATOR_PLATITUDES.length - 1);
    const preach = (): void => {
      const s = this.staffSprite;
      if (!s?.active) return;
      this.floatText(s.x, s.y - 400, FACILITATOR_PLATITUDES[line % FACILITATOR_PLATITUDES.length]!, PALETTE_HEX.charcoal, 2400);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(7000, 11000), preach);
    };
    this.time.delayedCall(2000, preach);
  }

  /** PHP: the demeaning lesson — the teacher talks DOWN, forever. */
  private lessonsRunning = false;
  private spawnLessons(): void {
    if (this.tierId !== "php" || !this.dollhouse || this.lessonsRunning) return;
    this.lessonsRunning = true;
    let line = Phaser.Math.Between(0, THERAPIST_LESSONS.length - 1);
    const teach = (): void => {
      const s = this.staffSprite;
      if (!s?.active) return;
      this.floatText(s.x, s.y - 400, THERAPIST_LESSONS[line % THERAPIST_LESSONS.length]!, PALETTE_HEX.charcoal, 2600);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(7000, 11000), teach);
    };
    this.time.delayedCall(2600, teach);
  }

  private spawnBreakVignette(): void {
    const v = BREAK_VIGNETTES[Phaser.Math.Between(0, BREAK_VIGNETTES.length - 1)]!;
    const floorY = RESI_TOP + Math.round(0.9 * RESI_BREAK_H);
    const actors = v.actors
      .filter((a) => isArtLoaded(a.tex))
      .map((a) => new Character(this, a.x * GAME.width, floorY, a.tex));
    if (actors.length === 0) return;
    let line = 0;
    const speak = (): void => {
      if (!actors[0]?.active) return;
      const who = actors[Math.min(line % 2, actors.length - 1)]!;
      this.floatText(who.x, who.y - 380, v.script[line % v.script.length]!, PALETTE_HEX.charcoal, 2000);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(4500, 7500), speak);
    };
    this.time.delayedCall(2500, speak);
  }

  private spawnLobbyist(): void {
    if (this.lobbyist) return;
    const x = GAME.width * 0.47;
    const y = DETOX_TOP + Math.round(0.88 * DETOX_PANEL_MED_H);
    this.lobbyist = new Character(this, x, y, resolvePatientTexture(7));
    const plead = (): void => {
      if (!this.lobbyist?.active) return;
      const line = MED_WINDOW_SYMPTOMS[Phaser.Math.Between(0, MED_WINDOW_SYMPTOMS.length - 1)]!;
      this.floatText(x, y - 380, `“${line}”`, PALETTE_HEX.charcoal, 2400);
      // Ten symptoms, three answers — the nurse replies half the time, if hired.
      if (this.staffSprite && Phaser.Math.Between(1, 100) <= 50) {
        this.time.delayedCall(2000, () => {
          if (!this.staffSprite?.active) return;
          const reply = MED_NURSE_REPLIES[Phaser.Math.Between(0, MED_NURSE_REPLIES.length - 1)]!;
          this.floatText(this.staffSprite.x, this.staffSprite.y - 380, reply, PALETTE_HEX.teal, 1600);
        });
      }
      this.time.delayedCall(Phaser.Math.Between(5000, 9000), plead);
    };
    this.time.delayedCall(1500, plead);
  }

  private placeFlower(x: number, y: number): void {
    const flower = this.add
      .text(x, y - 150, "❀", { fontSize: "60px", color: PALETTE_HEX.coralRed })
      .setOrigin(0.5, 1)
      .setDepth(y + 40);
    this.tweens.add({ targets: flower, alpha: 0, delay: 2600, duration: 900, onComplete: () => flower.destroy() });
  }

  private syncStaff(): void {
    const level = runtime.state.run.tiers[this.tierId].automatorLevel;
    if (level <= 0) return;
    this.spawnNurse();
    this.spawnPlatitudes();
    this.spawnLessons();
    this.spawnCheckinTheater();
    this.spawnIopFacilitator();
    this.spawnHouseMeeting();
    if (!this.staffSprite) {
      // Dollhouse: the tier's automator at its configured station (detox:
      // med-window nurse; residential: the facilitator mid-circle).
      const sx = this.dh ? this.dh.staff.x : 130;
      const sy = this.dh ? this.dh.staff.y : LAYOUT.floorY;
      const tex = this.dh && isArtLoaded(this.dh.staff.tex) ? this.dh.staff.tex : "person-staff";
      this.staffSprite = new Character(this, sx, sy, tex);
      if (this.dh?.staff.scale) this.staffSprite.setScale(this.dh.staff.scale);
      this.staffBadge = this.add
        .text(sx, sy - (this.dh?.staff.scale ? 300 * this.dh.staff.scale : 300), "", {
          fontFamily: "Georgia, serif",
          fontSize: "32px",
          color: PALETTE_HEX.gold,
        })
        .setOrigin(0.5)
        .setDepth(LAYOUT.floorY + 20);
    }
    this.staffBadge?.setText("★".repeat(level));
  }

  /** The M8 transparency window. */
  private syncRiskPanel(): void {
    if (!this.riskPanel) return;
    const b = relapseBreakdown(runtime.state, this.tierId);
    const parts = [
      `RELAPSE RISK ${b.total.toFixed(0)}%`,
      `base ${b.base}  crowd +${b.overcrowd}  issues +${b.undertreatAvg.toFixed(0)}`,
      `policy ${b.policy >= 0 ? "+" : ""}${b.policy}  staff ${b.fullStaff}  heat ${b.lowHeat}`,
    ];
    // v0.5: show the broker "dirty-house tax" only once it applies — the satire
    // is watching your overflow-selling raise your patients' relapse.
    if (b.broker > 0) parts.push(`broker +${b.broker.toFixed(0)}`);
    // v0.6: the irony engine — your criminal supply chain raises relapse too.
    if (b.criminal > 0) parts.push(`crime +${b.criminal.toFixed(0)}`);
    this.riskPanel.setText(parts.join("\n"));
  }

  private installActions(): void {
    installTierActions(this.overlay, this.tierId, this);
  }

  /** Rising fade-out text. `holdMs` keeps it readable in place first (speech). */
  floatText(x: number, y: number, msg: string, color: string, holdMs = 0, size = 42): void {
    const t = this.add
      .text(x, y, msg, {
        fontFamily: "Georgia, serif",
        fontSize: `${size}px`,
        fontStyle: "bold",
        color,
        // Held text is SPEECH — give it a paper bubble so it reads over art.
        ...(holdMs > 0 ? { backgroundColor: PALETTE_HEX.paper, padding: { x: 16, y: 10 } } : {}),
      })
      .setOrigin(0.5, 1)
      .setDepth(3000);
    // keep speech bubbles on-canvas (edge actors would clip otherwise)
    t.x = Math.max(t.displayWidth / 2 + 16, Math.min(GAME.width - t.displayWidth / 2 - 16, t.x));
    this.tweens.add({
      targets: t,
      y: y - 90,
      alpha: 0,
      delay: holdMs,
      duration: 1000,
      ease: "Cubic.easeOut",
      onComplete: () => t.destroy(),
    });
  }
}
